<template>
  <div>
    <multiple-select
      ref="selector"
      v-model="workgroupCentralSelector"
      :all-items="allowSelectMultipleWorkgroups && allowSelectWorkgroups && moreThanOneWorkgroup"
      :all-items-count="allWorkgroups.length"
      :all-items-selected="allWorkgroupsSelected"
      :all-items-value="allWorkgroups"
      :clearable="clearable"
      :disabled="disabled"
      :items="selectorItems"
      :tabindex="tabindex"
      all-items-class="font-weight-bold"
      all-items-label="Todos los workgroups"
      item-key="id"
      item-label="label"
      item-value="value"
      item-value-key="composedId"
      popperClass="tall"
    />
  </div>
</template>

<script>
import MultipleSelect from "@/components/common/forms/MultipleSelect";
import uniq from "lodash/uniq";
import { generateWorkgroupCentralSelectItem } from "@/utils/callcenter";
import { mapGetters } from "vuex";

export default {
  name: "WorkgroupCentralSelector",
  components: { MultipleSelect },
  props: {
    disabled: {
      type: Boolean,
    },
    allowSelectMultipleWorkgroups: {
      type: Boolean,
      default: false,
    },
    allowSelectMultipleCentrals: {
      type: Boolean,
      default: false,
    },
    allowSelectWorkgroups: {
      type: Boolean,
      default: true,
    },
    showCentrals: {
      type: Boolean,
      default: true,
    },
    allowEmpty: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
    allowedWorkgroup: {
      type: Number,
    },
    disabledCentralIds: {
      type: Array,
      default: () => [],
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    tabindex: {
      type: String,
    },
  },
  methods: {
    focus() {
      this.$nextTick(() => {
        if (this.$refs.selector) {
          this.$refs.selector.focus();
        }
      });
    },
    isCentralDisabled(central) {
      if (this.disabledCentralIds && this.disabledCentralIds.length > 0) {
        return this.disabledCentralIds.includes(central.id);
      }
      return false;
    },
    loadSelectorItems() {
      const items = [];
      this.workgroups.forEach((wg) => {
        if (this.allowedWorkgroup && this.allowedWorkgroup !== wg.id) {
          return;
        }
        items.push({
          id: wg.id,
          value: generateWorkgroupCentralSelectItem(wg.id),
          label: wg.name,
          class: "font-weight-bold",
          disabled: !this.allowSelectWorkgroups,
        });
        if (this.showCentrals) {
          wg.centrals.forEach((central) => {
            items.push({
              id: `${wg.id}_${central.id}`,
              value: generateWorkgroupCentralSelectItem(wg.id, central.id),
              label: `${central.name}`,
              class: "pl-4",
              disabled:
                (wg.centrals.length === 1 && this.allowSelectWorkgroups) ||
                this.isCentralDisabled(central),
            });
          });
        }
      });
      this.selectorItems = items;
    },
  },
  computed: {
    ...mapGetters("app", ["workgroups"]),
    moreThanOneWorkgroup() {
      return this.workgroups.length > 1;
    },
    allWorkgroups() {
      return this.workgroups.map((workgroup) => generateWorkgroupCentralSelectItem(workgroup.id));
    },
    allWorkgroupsSelected() {
      return (
        this.allowSelectMultipleWorkgroups &&
        this.allowSelectWorkgroups &&
        this.moreThanOneWorkgroup &&
        this.workgroupCentralSelector.filter((i) => !i.centralId).length ===
          this.allWorkgroups.length
      );
    },
  },
  watch: {
    value(v) {
      if (v !== this.workgroupCentralSelector) {
        this.workgroupCentralSelector = v;
      }
    },
    workgroupCentralSelector(v, oldV) {
      if (v === oldV) {
        return;
      }

      let selected = v || [];

      if (!this.allowEmpty && selected.length === 0 && (oldV || []).length > 0) {
        this.workgroupCentralSelector = [...oldV];
        return;
      }

      const lastAction = selected[selected.length - 1];
      if (lastAction && lastAction.centralId) {
        // If last action is a central
        if (!this.allowSelectMultipleCentrals && selected.filter((i) => i.centralId).length > 1) {
          // If we don't allow selecting multiple centrals, keep only the last one
          selected = selected.filter((i) => i.centralId === lastAction.centralId);
        } else {
          // Clean workgroup selection
          if (selected.filter((i) => !i.centralId).length > 0) {
            selected = selected.filter((i) => i.centralId);
          }
          // Clean centrals from other workgroups different from the last one
          if (uniq(selected.map((i) => i.wgId)).length > 1) {
            selected = selected.filter((i) => i.wgId === lastAction.wgId);
          }
        }
      } else if (lastAction) {
        // If last action is a workgroup
        // Clean all selected centrals
        if (
          selected.filter((i) => !i.centralId).length > 0 &&
          selected.filter((i) => i.centralId).length > 0
        ) {
          selected = selected.filter((i) => !i.centralId);
        }
        // Allow only one workgroup selected if allowSelectMultipleWorkgroups is false
        if (
          !this.allowSelectMultipleWorkgroups &&
          selected.filter((i) => !i.centralId).length > 1
        ) {
          selected = selected.filter((i) => i.wgId === lastAction.wgId);
        }
      }

      this.workgroupCentralSelector = selected;
      this.$emit("input", selected);
      this.$emit("change", selected);

      if (!this.allowSelectMultipleWorkgroups && !this.allowSelectMultipleCentrals) {
        this.$refs.selector.close();
      }
    },
  },
  created() {
    if (this.value && this.value.length > 0) {
      this.workgroupCentralSelector = this.value;
    }
    this.loadSelectorItems();
  },
  data() {
    return {
      workgroupCentralSelector: [],
      selectorItems: [],
    };
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .el-select__tags {
    flex-wrap: nowrap;
  }
  .el-tag__close {
    display: none;
  }
}
</style>
